import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Stack,
  Button,
  Switch,
  TextField,
  Paper,
  Grid,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { toast } from 'react-toastify';
import {
  Radio,
  Copy,
  Check,
  ShieldCheck,
  CheckCircle2,
  PhoneCall,
  Wifi,
  Zap,
  Lightbulb,
  Tv,
  GraduationCap,
  Wallet,
  CreditCard,
  Receipt,
} from 'lucide-react';
import { getAppSetting, updateAppSetting } from '../../../api/services';
import UssdUnactivatedState from './UssdUnactivatedState';
import UssdSuspendedState from './UssdSuspendedState';

const AppleSwitch = styled(Switch)(({ size }) => {
  const isSmall = size === 'small';
  const width = isSmall ? 38 : 46;
  const height = isSmall ? 22 : 26;
  const thumbSize = isSmall ? 18 : 22;
  const translate = isSmall ? 16 : 20;

  return {
    width,
    height,
    padding: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '&:active': {
      '& .MuiSwitch-thumb': {
        width: thumbSize + 3,
      },
      '& .MuiSwitch-switchBase.Mui-checked': {
        transform: `translateX(${translate - 3}px)`,
      },
    },
    '& .MuiSwitch-switchBase': {
      padding: 2,
      transitionDuration: '240ms',
      '&.Mui-checked': {
        transform: `translateX(${translate}px)`,
        color: '#FFFFFF',
        '& + .MuiSwitch-track': {
          backgroundColor: '#34C759',
          opacity: 1,
          border: 0,
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: thumbSize,
      height: thumbSize,
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2), 0 1px 1px rgba(0, 0, 0, 0.12)',
      transition: 'width 200ms ease, transform 200ms ease',
    },
    '& .MuiSwitch-track': {
      borderRadius: height / 2,
      backgroundColor: '#E5E7EB',
      opacity: 1,
      transition: 'background-color 240ms ease',
      boxSizing: 'border-box',
    },
  };
});

export default function UssdSettingSection({ onSave, subscription, activeApp }) {
  const { activeApp: reduxActiveApp } = useSelector((state) => state.auth);
  let effectiveApp = activeApp || reduxActiveApp;
  if (!effectiveApp) {
    try {
      const bootstrapped = sessionStorage.getItem('bootstrappedApp');
      if (bootstrapped) {
        effectiveApp = JSON.parse(bootstrapped);
      } else {
        const storedActive = localStorage.getItem('activeApp');
        if (storedActive) {
          effectiveApp = JSON.parse(storedActive);
        }
      }
    } catch (e) {
      // ignore
    }
  }

  const appName = effectiveApp?.name || 'VTU';
  const appSlug = effectiveApp?.slug || 'vtu';

  // Active status based strictly on Super Admin subscription
  const isSubActive = subscription?.status === 'active';
  const isSubSuspended =
    subscription?.status === 'suspended' ||
    subscription?.status === 'past-due' ||
    subscription?.status === 'past_due' ||
    subscription?.status === 'paused';

  const [saving, setSaving] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  // Form states populated dynamically from active app
  const [formData, setFormData] = useState({
    enabled: true,
    businessName: `${appName} VTU`,
    welcomePrompt: `Welcome to ${appName}!\n1. Buy Data\n2. Buy Airtime\n3. Electricity\n4. Cable TV\n5. Exam PIN\n6. Wallet Balance\n7. Fund Wallet\n8. History`,
    allowUserRegistration: true,
    defaultEmailDomain: `${appSlug}.com.ng`,
    dialFee: 0,
    completionFee: 0,
    sessionFee: 0,
    sessionTtlSeconds: 180,
    enabledServices: {
      data: true,
      airtime: true,
      electricity: true,
      cable: true,
      exam: true,
      balanceCheck: true,
      fundWallet: true,
      history: true,
    },
  });

  useEffect(() => {
    let isMounted = true;
    const fetchSavedSettings = async () => {
      try {
        const res = await getAppSetting();
        if (res && res.ok && res.data?.data) {
          const savedUssd = res.data.data.ussd_settings;
          if (savedUssd && isMounted) {
            setFormData((prev) => ({
              ...prev,
              ...savedUssd,
              dialFee: savedUssd.dialFee ?? savedUssd.sessionFee ?? 0,
              completionFee: savedUssd.completionFee ?? 0,
              enabledServices: {
                ...prev.enabledServices,
                ...(savedUssd.enabledServices || {}),
              },
            }));
          }
        }
      } catch (e) {
        // ignore
      }
    };
    fetchSavedSettings();
    return () => {
      isMounted = false;
    };
  }, []);

  // Read-only parameters provisioned by Super Admin
  const superAdminProvisioned = {
    shortCode: subscription?.serviceDetails?.shortCode || '*384*224#',
    providerName: subscription?.serviceDetails?.provider || "Africa's Talking (Dedicated Gateway)",
    subscriptionStatus: subscription?.status || 'active',
    planName: subscription?.product?.name || 'Dedicated USSD Channel (Monthly)',
    expiresAt: subscription?.expiresAt ? new Date(subscription.expiresAt).toLocaleDateString() : 'N/A',
    callbackUrl: `${window.location.origin.replace('admin.', '').replace('3000', '8080')}/api/v1/integrations/ussd`,
  };

  const servicesList = [
    {
      key: 'data',
      label: 'Data Bundles',
      desc: 'MTN, Airtel, Glo, 9mobile data',
      icon: <Wifi size={18} />,
      color: '#0071E3',
      bg: '#EFF6FF',
    },
    {
      key: 'airtime',
      label: 'Airtime VTU',
      desc: 'Instant airtime recharge',
      icon: <Zap size={18} />,
      color: '#F59E0B',
      bg: '#FEF3C7',
    },
    {
      key: 'electricity',
      label: 'Electricity Bills',
      desc: 'Prepaid & postpaid tokens',
      icon: <Lightbulb size={18} />,
      color: '#EAB308',
      bg: '#FEFCE8',
    },
    {
      key: 'cable',
      label: 'Cable TV',
      desc: 'DStv, GOtv, Startimes',
      icon: <Tv size={18} />,
      color: '#8B5CF6',
      bg: '#F5F3FF',
    },
    {
      key: 'exam',
      label: 'Exam PINs',
      desc: 'WAEC, NECO, JAMB tokens',
      icon: <GraduationCap size={18} />,
      color: '#EC4899',
      bg: '#FDF2F8',
    },
    {
      key: 'balanceCheck',
      label: 'Check Balance',
      desc: 'Instant real-time inquiry',
      icon: <Wallet size={18} />,
      color: '#10B981',
      bg: '#ECFDF5',
    },
    {
      key: 'fundWallet',
      label: 'Fund Wallet',
      desc: 'Virtual bank accounts',
      icon: <CreditCard size={18} />,
      color: '#06B6D4',
      bg: '#ECFEFF',
    },
    {
      key: 'history',
      label: 'Order History',
      desc: 'Recent purchase receipts',
      icon: <Receipt size={18} />,
      color: '#6366F1',
      bg: '#EEF2FF',
    },
  ];

  const enabledCount = servicesList.filter((s) => formData.enabledServices?.[s.key]).length;

  const handleCopy = (text, fieldName) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    toast.info(`Copied ${fieldName} to clipboard`);
    setTimeout(() => setCopiedField(null), 2500);
  };

  const handleServiceToggle = (serviceKey) => {
    setFormData((prev) => ({
      ...prev,
      enabledServices: {
        ...prev.enabledServices,
        [serviceKey]: !prev.enabledServices[serviceKey],
      },
    }));
  };

  const handleEnableAllServices = () => {
    setFormData((prev) => {
      const allEnabled = {};
      servicesList.forEach((s) => {
        allEnabled[s.key] = true;
      });
      return {
        ...prev,
        enabledServices: allEnabled,
      };
    });
    toast.info('All USSD services enabled');
  };

  const handleDisableAllServices = () => {
    setFormData((prev) => {
      const allDisabled = {};
      servicesList.forEach((s) => {
        allDisabled[s.key] = false;
      });
      return {
        ...prev,
        enabledServices: allDisabled,
      };
    });
    toast.info('All USSD services disabled');
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...formData,
        sessionFee: formData.dialFee, // maintain backward compatibility
      };
      if (onSave) {
        await onSave(payload);
      } else {
        await updateAppSetting({
          ussd_settings: payload,
        });
      }
      toast.success('USSD Channel settings saved successfully!');
    } catch (err) {
      toast.error('Failed to save USSD settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={3.5} sx={{ width: '100%', pb: 6 }}>
      {/* STATE 1: UNLOCKED / ACTIVE SUBSCRIPTION */}
      {isSubActive && (
        <>
          {/* 1. Apple Header Bar with Channel Summary & Save Button */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: '24px',
              bgcolor: '#FFFFFF',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 2px 16px rgba(0,0,0,0.03)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              justifyContent="space-between"
              spacing={2.5}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: '16px',
                    bgcolor: '#0071E3',
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 6px 18px rgba(0, 113, 227, 0.28)',
                    flexShrink: 0,
                  }}
                >
                  <Radio size={28} />
                </Box>
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 800,
                        color: '#111827',
                        letterSpacing: '-0.025em',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' },
                      }}
                    >
                      USSD Offline Channel
                    </Typography>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.75,
                        px: 1.25,
                        py: 0.35,
                        borderRadius: '999px',
                        bgcolor: '#ECFDF5',
                        color: '#059669',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                      }}
                    >
                      <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10B981' }} />
                      Active
                    </Box>
                  </Stack>
                  <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.25, fontSize: '0.88rem' }}>
                    {superAdminProvisioned.providerName} • Dedicated Short Code: {superAdminProvisioned.shortCode}
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
                <Button
                  variant="contained"
                  disabled={saving}
                  onClick={handleSave}
                  startIcon={saving && <CircularProgress size={16} color="inherit" />}
                  sx={{
                    bgcolor: '#0071E3',
                    color: '#FFFFFF',
                    borderRadius: '999px',
                    px: 3.5,
                    py: 1.1,
                    fontSize: '0.92rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    boxShadow: 'none',
                    width: { xs: '100%', sm: 'auto' },
                    '&:hover': {
                      bgcolor: '#0077ED',
                      boxShadow: 'none',
                    },
                  }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Stack>
          </Paper>

          {/* 2. Apple Card: Connected Gateway Details & Channel Status */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.25, sm: 2.75 },
              borderRadius: '24px',
              bgcolor: '#FFFFFF',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 2px 16px rgba(0,0,0,0.03)',
            }}
          >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '14px',
                      bgcolor: '#ECFDF5',
                      color: '#059669',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <ShieldCheck size={24} />
                  </Box>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#111827', fontSize: '1rem' }}>
                      Dedicated Short Code Gateway
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#6B7280', display: 'block', fontSize: '0.8rem' }}>
                      Dedicated short code channel provisioned and active for your platform
                    </Typography>
                  </Box>
                </Stack>

                {/* Apple-styled Channel State Pill Switch */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    bgcolor: formData.enabled ? '#F0FDF4' : '#F9FAFB',
                    p: '6px 14px',
                    borderRadius: '999px',
                    border: '1px solid',
                    borderColor: formData.enabled ? 'rgba(34, 197, 94, 0.25)' : 'rgba(0,0,0,0.06)',
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 700,
                        color: formData.enabled ? '#15803D' : '#6B7280',
                        fontSize: '0.84rem',
                        lineHeight: 1.2,
                      }}
                    >
                      {formData.enabled ? 'Channel Online' : 'Channel Paused'}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: formData.enabled ? '#16A34A' : '#9CA3AF',
                        display: 'block',
                        fontSize: '0.7rem',
                      }}
                    >
                      {formData.enabled ? 'Accepting customer dials' : 'Temporarily paused'}
                    </Typography>
                  </Box>
                  <AppleSwitch
                    disableRipple
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  />
                </Box>
              </Stack>

              <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

              <Paper
                variant="outlined"
                sx={{
                  p: { xs: 2, sm: 2.25 },
                  borderRadius: '16px',
                  bgcolor: '#F9FAFB',
                  borderColor: 'rgba(0,0,0,0.08)',
                }}
              >
                <Typography variant="caption" sx={{ color: '#6B7280', fontWeight: 600, fontSize: '0.74rem' }}>
                  ASSIGNED DIAL CODE
                </Typography>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.75 }}>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#111827', letterSpacing: '0.04em' }}>
                    {superAdminProvisioned.shortCode}
                  </Typography>
                  <Tooltip title="Copy Short Code">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleCopy(superAdminProvisioned.shortCode, 'Short Code')}
                      startIcon={copiedField === 'Short Code' ? <Check size={14} /> : <Copy size={14} />}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        borderColor: '#D1D5DB',
                        color: '#374151',
                        bgcolor: '#FFFFFF',
                        px: 1.75,
                        '&:hover': { bgcolor: '#F3F4F6' },
                      }}
                    >
                      {copiedField === 'Short Code' ? 'Copied' : 'Copy'}
                    </Button>
                  </Tooltip>
                </Stack>
              </Paper>
            </Stack>
          </Paper>

          {/* 3. Apple Card: Session & Transaction Service Charges (NEW FEATURE) */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.25, sm: 2.75 },
              borderRadius: '24px',
              bgcolor: '#FFFFFF',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 2px 16px rgba(0,0,0,0.03)',
            }}
          >
            <Stack spacing={2}>
              <Box>
                <Typography variant="caption" sx={{ color: '#374151', fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.04em' }}>
                  SESSION &amp; TRANSACTION SERVICE CHARGES
                </Typography>
                <Typography variant="body2" sx={{ color: '#6B7280', fontSize: '0.85rem', mt: 0.25 }}>
                  Set custom convenience fees charged to customer wallets when they dial/initiate a session and upon transaction completion.
                </Typography>
              </Box>

              <Grid container spacing={2}>
                {/* Dial / Session Initiation Fee */}
                <Grid item xs={12} sm={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: '16px',
                      borderColor: 'rgba(0,0,0,0.08)',
                      bgcolor: '#F9FAFB',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '10px',
                          bgcolor: '#EFF6FF',
                          color: '#0071E3',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <PhoneCall size={17} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827', fontSize: '0.9rem' }}>
                          Dial / Session Initiation Charge
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.72rem' }}>
                          Deducted from user wallet when dialing the short code
                        </Typography>
                      </Box>
                    </Stack>
                    <TextField
                      fullWidth
                      type="number"
                      value={formData.dialFee}
                      onChange={(e) => setFormData({ ...formData, dialFee: Math.max(0, Number(e.target.value)) })}
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ fontWeight: 700, color: '#111827' }}>₦</Typography>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>per dial / session</Typography>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      sx={{
                        bgcolor: '#FFFFFF',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          fontWeight: 700,
                          fontSize: '1.05rem',
                        },
                      }}
                    />
                  </Paper>
                </Grid>

                {/* Transaction Completion Fee */}
                <Grid item xs={12} sm={6}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      borderRadius: '16px',
                      borderColor: 'rgba(0,0,0,0.08)',
                      bgcolor: '#F9FAFB',
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.5 }}>
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: '10px',
                          bgcolor: '#ECFDF5',
                          color: '#059669',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <CheckCircle2 size={17} />
                      </Box>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111827', fontSize: '0.9rem' }}>
                          Transaction Completion Charge
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#6B7280', fontSize: '0.72rem' }}>
                          Surcharge added upon successful order / purchase
                        </Typography>
                      </Box>
                    </Stack>
                    <TextField
                      fullWidth
                      type="number"
                      value={formData.completionFee}
                      onChange={(e) => setFormData({ ...formData, completionFee: Math.max(0, Number(e.target.value)) })}
                      placeholder="0.00"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ fontWeight: 700, color: '#111827' }}>₦</Typography>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>per completed order</Typography>
                          </InputAdornment>
                        ),
                      }}
                      variant="outlined"
                      sx={{
                        bgcolor: '#FFFFFF',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          fontWeight: 700,
                          fontSize: '1.05rem',
                        },
                      }}
                    />
                  </Paper>
                </Grid>
              </Grid>

              {/* Pricing Formula Preview Banner */}
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '14px',
                  bgcolor: '#F0F7FF',
                  border: '1px solid rgba(0, 113, 227, 0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                }}
              >
                <Typography variant="caption" sx={{ color: '#0071E3', fontWeight: 700, fontSize: '0.8rem' }}>
                  💡 Customer Fee Formula:
                </Typography>
                <Typography variant="caption" sx={{ color: '#1E40AF', fontSize: '0.8rem' }}>
                  Customer pays <strong>₦{formData.dialFee || 0}</strong> on session dial + Product Price + <strong>₦{formData.completionFee || 0}</strong> upon completion.
                </Typography>
              </Box>
            </Stack>
          </Paper>

          {/* 4. Apple Card: Active Services on USSD Matrix */}
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, sm: 3 },
              borderRadius: '24px',
              bgcolor: '#FFFFFF',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 2px 16px rgba(0,0,0,0.03)',
            }}
          >
            <Stack spacing={2.5}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                spacing={1.5}
              >
                <Box>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', letterSpacing: '-0.015em' }}>
                      Active Services on USSD
                    </Typography>
                    <Box
                      sx={{
                        px: 1.25,
                        py: 0.25,
                        borderRadius: '999px',
                        bgcolor: '#F3F4F6',
                        color: '#4B5563',
                        fontSize: '0.72rem',
                        fontWeight: 700,
                      }}
                    >
                      {enabledCount} of {servicesList.length} enabled
                    </Box>
                  </Stack>
                  <Typography variant="body2" sx={{ color: '#6B7280', mt: 0.5, fontSize: '0.85rem' }}>
                    Toggle which VTU products and self-service menus are accessible over USSD.
                  </Typography>
                </Box>

                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    onClick={handleEnableAllServices}
                    sx={{
                      color: '#0071E3',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '8px',
                      px: 1.25,
                      py: 0.4,
                      bgcolor: '#F0F7FF',
                      '&:hover': { bgcolor: '#E0F0FE' },
                    }}
                  >
                    Enable All
                  </Button>
                  <Button
                    size="small"
                    onClick={handleDisableAllServices}
                    sx={{
                      color: '#6B7280',
                      fontSize: '0.78rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderRadius: '8px',
                      px: 1.25,
                      py: 0.4,
                      bgcolor: '#F3F4F6',
                      '&:hover': { bgcolor: '#E5E7EB' },
                    }}
                  >
                    Disable All
                  </Button>
                </Stack>
              </Stack>

              {/* Apple-styled Inset Grouped List */}
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: '18px',
                  bgcolor: '#FFFFFF',
                  borderColor: 'rgba(0,0,0,0.08)',
                  overflow: 'hidden',
                }}
              >
                <Stack divider={<Divider sx={{ borderColor: 'rgba(0,0,0,0.05)' }} />}>
                  {servicesList.map((svc) => {
                    const isChecked = !!formData.enabledServices[svc.key];

                    return (
                      <Box
                        key={svc.key}
                        onClick={() => handleServiceToggle(svc.key)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          px: { xs: 2, sm: 2.5 },
                          py: 1.6,
                          cursor: 'pointer',
                          transition: 'background-color 0.15s ease',
                          '&:hover': {
                            bgcolor: '#F9FAFB',
                          },
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={2}
                          sx={{ minWidth: 0, pr: 2 }}
                        >
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: '11px',
                              bgcolor: svc.bg,
                              color: svc.color,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              opacity: isChecked ? 1 : 0.6,
                              transition: 'opacity 0.2s ease',
                            }}
                          >
                            {svc.icon}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                color: isChecked ? '#111827' : '#4B5563',
                                fontSize: '0.92rem',
                                lineHeight: 1.3,
                              }}
                            >
                              {svc.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: isChecked ? '#6B7280' : '#9CA3AF',
                                fontSize: '0.8rem',
                                display: 'block',
                                lineHeight: 1.3,
                                mt: 0.25,
                              }}
                            >
                              {svc.desc}
                            </Typography>
                          </Box>
                        </Stack>

                        <Box
                          onClick={(e) => e.stopPropagation()}
                          sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
                        >
                          <AppleSwitch
                            size="small"
                            disableRipple
                            checked={isChecked}
                            onChange={() => handleServiceToggle(svc.key)}
                          />
                        </Box>
                      </Box>
                    );
                  })}
                </Stack>
              </Paper>
            </Stack>
          </Paper>

          {/* Branding & Registration configuration card hidden for now */}
        </>
      )}

      {/* STATE 2: PAUSED (AFTER GRACE PERIOD) */}
      {isSubSuspended && <UssdSuspendedState subscription={subscription} />}

      {/* STATE 3: LOCKED / UNPAID SUBSCRIPTION (MINIMAL APPLE STATE) */}
      {!isSubActive && !isSubSuspended && <UssdUnactivatedState />}
    </Stack>
  );
}

UssdSettingSection.propTypes = {
  onSave: PropTypes.func,
  subscription: PropTypes.object,
  activeApp: PropTypes.object,
};
